import {Country, CountryCurrencyConfig} from 'coreStore/data/configurations/types';
import {EEventSource, sourceMappings, EComponentSource} from '../events/IEvents';

/**
 * Represents Country Model
 */
class CountryModel {
	private country: Country;

	/**
	 * @constructor
	 * @param {Country} country - country model
	 */
	constructor(country: Country) {
		this.country = country;
	}

	/**
	 * Returns country code
	 * @returns {string}
	 * @memberof CountryModel
	 * @example
	 * const country = CountriesMgr.getCountryByCode('US');
	 * console.log(country.getCountryCode()); // US
	 */
	getCountryCode(): string {
		return this.country.countryCode;
	}

	/**
	 * Returns country continents
	 * @returns {string[]}
	 * @memberof CountryModel
	 */
	getContinents(): string[] {
		return this.country.continents;
	}

	/**
	 * Returns the Component Source from each area
	 * @param {EEventSource} area - price area
	 */
	getSourceMapping(area: EEventSource): EComponentSource {
		return sourceMappings[area];
	}

	/**
	 * Returns country region codes
	 * @returns {Object}
	 * @memberof CountryModel
	 * @example
	 * const country = CountriesMgr.getCountryByCode('US');
	 * console.log(country.getRegionCodes()); // {'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas'}
	 */
	getRegionCodes(): {[key: string]: string} {
		return this.country.regionCodes as unknown as {[key: string]: string};
	}

	/**
	 * Returns country currency code
	 * @returns {string}
	 * @memberof CountryModel
	 * @example
	 * const country = CountriesMgr.getCountryByCode('US');
	 * console.log(country.getCurrencyCode()); // USD
	 */
	getCurrencyCode(): string {
		return this.country.currencyCode;
	}

	/**
	 * Returns country currency symbol
	 * @returns {string}
	 * @memberof CountryModel
	 * @example
	 * const country = CountriesMgr.getCountryByCode('US');
	 * console.log(country.getCurrencySymbol()); // $
	 */
	getCurrencySymbol(): string {
		return this.country.currencySymbol;
	}

	/**
	 * Returns country hreflang language codes
	 * @returns {string[]} - array of hreflang language codes
	 * @memberof CountryModel
	 */
	getHreflangLanguageCodes(): string[] {
		//TODO: maybe we should use it in footer as well?
		return this.country.hreflangLanguageCodes || [];
	}

	/**
	 * Returns country currency config
	 * @returns {CountryCurrencyConfig}
	 */
	getCurrencyConfig(): CountryCurrencyConfig {
		return this.country.currencyConfig;
	}

	/**
	 * Returns country site id
	 * @returns {string}
	 */
	getSiteId(): string {
		return this.country.siteId;
	}

	/**
	 * Gets the social login buttons
	 * @returns {Array<String> | null} - social login buttons types or null
	 */
	getSocialLoginButtons(): string[] | null {
		return this.country.socialLoginButtons?.length ? this.country.socialLoginButtons.split(',') : null;
	}

	/**
	 * Returns all currencies from country currency config
	 * @returns {String[]}
	 */
	getCurrenciesFromConfig(): string[] {
		let currencies = [];
		const currencyConfig = this.getCurrencyConfig();
		const countryCurrency = this.getCurrencyCode();

		if (currencyConfig.areas) {
			Object.keys(currencyConfig.areas).forEach((area) => {
				const areaConfig = currencyConfig.areas[area];

				if (Array.isArray(areaConfig)) {
					currencies.push(...areaConfig);
				} else {
					Object.values(areaConfig).forEach((tmpCurrencies) => {
						currencies.push(...tmpCurrencies);
					});
				}
			});
		}

		return currencies
			.filter((currency) => currency !== countryCurrency)
			.filter((currency, index, self) => self.indexOf(currency) === index);
	}

	/**
	 * Returns true if DDP option is enabled for this country, false otherwise
	 * @returns {boolean}
	 */
	isDDPOptionEnabled(): boolean {
		return this.country.isDDPOptionEnabled;
	}

	/**
	 * Returns true if DDP on PDP option is enabled for this country, false otherwise
	 * @returns {boolean}
	 */
	isDDPBadgeEnabledOnPDP(): boolean {
		return this.country.isDDPBadgeEnabledOnPDP;
	}

	/**
	 * Returns true if Virtual Try On is disabled for this country, false otherwise
	 * @returns {boolean}
	 */
	isVirtualTryOnDisabled(): boolean {
		return this.country.isVirtualTryOnDisabled;
	}

	/**
	 * Returns true if Virtual Mirror is disabled for this country, false otherwise
	 * @returns {boolean}
	 */
	isVirtualMirrorDisabled(): boolean {
		return this.country.isVirtualMirrorDisabled;
	}

	/**
	 * Returns true if Virtual Mirror is disabled for this country, false otherwise
	 * @returns {boolean}
	 */
	isBeautyVirtualTryOnDisabled(): boolean {
		return this.country.isBeautyVirtualTryOnDisabled;
	}

	/**
	 * Returns the tax rate for this country
	 * @returns {number}
	 */
	getTaxRate(): number {
		return this.country.taxRate;
	}

	/**
	 * Returns the locale settings for this country
	 * @returns {Object}
	 */
	getLocaleSettings(): object {
		return this.country.localeSettings;
	}

	/**
	 * Returns true if for the given area all currency configs have more than one price
	 * @param {EEventSource} area - price area
	 */
	isMultiCurrency(area: EEventSource): boolean {
		const source = this.getSourceMapping(area);
		const currencyConfig = this.getCurrencyConfig();
		const currencyConfigAreas = currencyConfig?.areas;
		const currencyConfigArea = source && currencyConfigAreas && currencyConfig?.areas[source];

		return currencyConfigArea?.standard?.length > 1 &&
			currencyConfigArea?.sale?.length > 1;
	}

	/**
	 * Returns the locale settings for this country
	 * @returns {Object}
	 */
	getPriceBooksGroup(): string {
		return this.country?.priceBooksGroup || '';
	}

	/**
	 * Returns available locales for the country
	 * @returns {Array}
	 */
	getAvailableLanguages(): string[] {
		return this.country?.availableLanguages || [];
	}

	/**
	 * Returns default language for the country
	 * @returns {string}
	 */
	getDefaultLanguage(): string {
		return this.country?.availableLanguages?.[0] || '';
	}

	/**
	 * Returns true if the country has available languages set
	 * @returns {boolean}
	 */
	hasAvailableLanguages(): boolean {
		return this.getAvailableLanguages().length > 0;
	}

	/**
	 * Is Language available for that country
	 * @param {string} languageCode - selected language
	 * @returns {boolean}
	 */
	isLanguageAvailable(languageCode: string): boolean {
		return this.country?.availableLanguages?.includes(languageCode);
	}

	/**
	 * Returns external URL for the country
	 * @returns {string}
	 */
	getExternalUrl(): string {
		return this.country?.externalUrl || '';
	}

	/**
	 * Returns country unit of measure
	 * @returns {string}
	 */
	getUnitOfMeasure(): string {
		return this.country?.unitOfMeasure || '';
	}

	/**
	 * Returns the return fee for the country
	 * @returns {number} return fee, 0 when not configured
	 */
	getReturnFee(): number {
		return this.country?.returnFee || 0;
	}

	/**
	 * Returns the customer group IDs excluded from the return fee
	 * @returns {string[]} customer group IDs
	 */
	getReturnAuthCustomerGroupsToExclude(): string[] {
		return this.country?.returnAuthCustomerGroupsToExclude || [];
	}

	/**
	 * Returns the order total above which the return fee is not applied
	 * @returns {number|null} threshold, null when not configured
	 */
	getReturnOrderAmountThreshold(): number | null {
		return this.country?.returnOrderAmountThreshold || null;
	}
}

export default CountryModel;
